import React, { useEffect, useState } from 'react'
import MickeyTable from '../../../components/MickeyTable/MickeyTable'
import { Table, Button, Modal, Segment, Header, Icon } from 'semantic-ui-react'
import { toast } from 'react-toastify'
import MickeyPlaceholderLoading from '../../../components/PlaceholderLoading/PlaceholderLoading'

import useAxios from '../../../lib/auth/useAxios'
import MickeyButton from '../../../components/MickeyButton/MickeyButton'
import { useSelector } from 'react-redux'
import MickeyForm from '../../../components/Forms/MickeyForm'
import { PencilLine, Trash } from 'phosphor-react'

const AdminProductVariants = ({ product }) => {
  const [rows, setRows] = useState([])
  const [attributes, setAttributes] = useState([])
  const [attributeId, setAttributeId] = useState(null)
  const [openAddAttributeModal, setOpenAddAttributeModal] = useState(false)
  const [openEditAttributeModal, setOpenEditAttributeModal] = useState(false)
  const [editingAttributeId, setEditingAttributeId] = useState(null)
  const [openAddAttributeSelectionModal, setOpenAddAttributeSelectionModal] =
    useState(false)
  const [editingAttributeSelectionId, setEditingAttributeSelectionId] =
    useState()
  const [openEditAttributeSelectionModal, setOpenEditAttributeSelectionModal] =
    useState(false)
  const [loading, setLoading] = useState(false)
  const [reload, setReload] = useState(false)
  const axios = useAxios()
  const tenant = useSelector((state) => state.tenant)

  const addAttribute = async (newAttributeData) => {
    const response = await axios.post(`/main/attributes/`, {
      product_id: product?.id,
      name: newAttributeData.name,
      description: newAttributeData.description,
    })
    setReload(true)
  }
  const addAttributeSelection = async (attributeSelectionData) => {
    const response = await axios.post(`/main/attribute-selections/`, {
      attribute_id: attributeId,
      value: attributeSelectionData.value,
    })
    setReload(true)
  }

  const deleteAttributeSelection = async (attributeSelectionId) => {
    if (!window.confirm('Are you sure you want to delete this attribute selection?')) {
      return
    }
    try {
      await axios.delete(`/main/attribute-selections/${attributeSelectionId}/`)
      setReload(!reload)
      toast.success('Removed variant!')
    } catch (error) {
      console.error('Error deleting attribute selection:', error)
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message))
    }
  }

  const deleteAttribute = async (attributeId) => {
    if (!window.confirm('Are you sure you want to delete this attribute? This will also delete all its selections.')) {
      return
    }
    try {
      await axios.delete(`/main/attributes/${attributeId}/`)
      setReload(!reload)
      toast.success('Attribute deleted!')
    } catch (error) {
      console.error('Error deleting attribute:', error)
      toast.error('Failed to delete attribute: ' + (error.response?.data?.detail || error.message))
    }
  }

  const buildRows = (attributes) => {
    let tempRows = []
    let rowIndex = 0
    let attributeIndex = 0

    tempRows.push([...Array(attributes.length).fill(null)])

    const maxAttributeSelectionsLength = attributes.reduce(
      (maxLength, attribute) => {
        const currentLength = attribute.values.length
        return currentLength > maxLength ? currentLength : maxLength
      },
      0
    )

    const buildRow = () => {
      if (rowIndex !== 0) {
        tempRows.push([...Array(attributes.length + 1).fill(null)])
      }
      attributes.forEach((attribute, index) => {
        if (attribute?.values[rowIndex]?.value) {
          tempRows[rowIndex][attributeIndex] = {
            value: attribute.values[rowIndex].value,
            id: attribute.values[rowIndex].id,
          }
        }
        if (rowIndex === attribute.values.length) {
          tempRows[rowIndex][attributeIndex] = 'Add Attribute Selection'
        }
        attributeIndex = attributeIndex + 1
      })
      rowIndex++
      attributeIndex = 0

      if (rowIndex <= maxAttributeSelectionsLength) {
        buildRow()
      }
    }
    buildRow()
    setRows(tempRows)
  }

  useEffect(() => {
    const getAttributes = async () => {
      setLoading(true)
      try {
        const response = await axios.get(`/main/attributes/`, {
          params: {
            product_id: product?.id,
          },
        })
        if (response.status === 200) {
          setAttributes(response.data.results)
          buildRows(response.data.results)
        }
      } finally {
        setLoading(false)
      }
    }
    if (product) {
      getAttributes()
    }
  }, [product, reload])

  return (
    <div style={{ overflow: 'scroll' }}>
      <Modal
        open={openEditAttributeSelectionModal}
        onClose={() => setOpenEditAttributeSelectionModal(false)}
        onOpen={() => setOpenEditAttributeSelectionModal(true)}
      >
        <Modal.Header>Edit Attribute Selection</Modal.Header>
        <Modal.Content>
          <MickeyForm
            formName={'AttributeSelectionForm'}
            model={'AttributeSelection'}
            reload={reload}
            instanceId={editingAttributeSelectionId}
            setReload={setReload}
            buttonFloatedRight={true}
            successMessage={'Attribute Selection Updated!'}
            addToSubmit={() => {
              setOpenEditAttributeSelectionModal(false)
              setReload(!reload)
            }}
            failureMessage={'Error updating attribute selection.'}
            url={'/main/attribute-selections/'}
          />
        </Modal.Content>
      </Modal>
      <Modal
        open={openAddAttributeSelectionModal}
        onClose={() => setOpenAddAttributeSelectionModal(false)}
        onOpen={() => setOpenAddAttributeSelectionModal(true)}
      >
        <Modal.Header>Add Attribute Selection</Modal.Header>
        <Modal.Content>
          <MickeyForm
            formName={'AttributeSelectionForm'}
            model={'AttributeSelection'}
            reload={reload}
            setReload={setReload}
            buttonFloatedRight={true}
            successMessage={'Attribute Selection Added!'}
            addToSubmit={() => {}}
            onSubmit={(e) => {
              addAttributeSelection(e)
              setOpenAddAttributeSelectionModal(false)
            }}
            failureMessage={'Error adding attribute selection.'}
            url={'/main/attribute-selections/'}
          />
        </Modal.Content>
      </Modal>
      <Modal
        open={openAddAttributeModal}
        onClose={() => setOpenAddAttributeModal(false)}
        onOpen={() => setOpenAddAttributeModal(true)}
      >
        <Modal.Header>Add Attribute</Modal.Header>
        <Modal.Content>
          <MickeyForm
            formName={'AttributeForm'}
            model={'Attribute'}
            reload={reload}
            setReload={setReload}
            buttonFloatedRight={true}
            successMessage={'Attribute Added!'}
            onSubmit={(e) => {
              addAttribute(e)
              setOpenAddAttributeModal(false)
            }}
            failureMessage={'Error adding attribute.'}
            url={'/main/attributes/'}
          />
        </Modal.Content>
      </Modal>
      <Modal
        open={openEditAttributeModal}
        onClose={() => setOpenEditAttributeModal(false)}
        onOpen={() => setOpenEditAttributeModal(true)}
      >
        <Modal.Header>Edit Attribute</Modal.Header>
        <Modal.Content>
          <MickeyForm
            formName={'AttributeForm'}
            model={'Attribute'}
            reload={reload}
            instanceId={editingAttributeId}
            setReload={setReload}
            buttonFloatedRight={true}
            successMessage={'Attribute Updated!'}
            addToSubmit={() => {
              setOpenEditAttributeModal(false)
              setReload(!reload)
            }}
            failureMessage={'Error updating attribute.'}
            url={'/main/attributes/'}
          />
        </Modal.Content>
      </Modal>
      {loading && <MickeyPlaceholderLoading />}
      {!loading && (
        <Table
          celled
          className="mickey-table"
          style={{ margin: '3em', marginTop: '5em' }}
        >
          {!product ? (
            <Table.Body>
              <Table.Row>
                <Table.Cell colSpan={16}>
                  <Segment placeholder className={'mickey-table-placeholder'}>
                    <Header icon>
                      <Icon name={'warning sign'} />
                      Select a product to view its variants.
                    </Header>
                  </Segment>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          ) : (
            <>
              <Table.Header>
                <Table.Row>
                  {attributes.map((attribute) => {
                    return (
                      <Table.Cell key={attribute.id}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <b>{attribute.name}</b>
                          <Button.Group size={'mini'} style={{ marginLeft: '10px' }}>
                            <Button
                              circular
                              size={'mini'}
                              icon={<PencilLine />}
                              onClick={() => {
                                setEditingAttributeId(attribute.id)
                                setOpenEditAttributeModal(true)
                              }}
                            />
                            <Button
                              circular
                              color="red"
                              size={'mini'}
                              icon={<Trash />}
                              onClick={() => {
                                deleteAttribute(attribute.id)
                              }}
                            />
                          </Button.Group>
                        </div>
                      </Table.Cell>
                    )
                  })}
                  <Table.Cell>
                    {' '}
                    <MickeyButton
                      content={'Add Attribute'}
                      onClick={() => {
                        setOpenAddAttributeModal(true)
                      }}
                      style={{
                        cursor: 'pointer',
                        '--primaryColor': tenant?.deployment_primary_color,
                        '--secondaryColor': tenant?.deployment_secondary_color,
                      }}
                      innerStyle={{}}
                    />
                  </Table.Cell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => {
                  return (
                    <Table.Row>
                      {row.map((cell, index) => {
                        if (cell === 'Add Attribute Selection') {
                          return (
                            <Table.Cell>
                              <MickeyButton
                                content={'Add Variant'}
                                size={'mini'}
                                onClick={() => {
                                  setOpenAddAttributeSelectionModal(true)
                                  setAttributeId(attributes[index].id)
                                }}
                                style={{
                                  margin: 0,
                                  cursor: 'pointer',
                                  '--primaryColor':
                                    tenant?.deployment_primary_color,
                                  '--secondaryColor':
                                    tenant?.deployment_secondary_color,
                                }}
                              />
                            </Table.Cell>
                          )
                        } else {
                          return (
                            <Table.Cell>
                              {cell && cell.value}
                              {cell && (
                                <Button.Group
                                  size={'mini'}
                                  style={{ marginLeft: '5px' }}
                                >
                                  <Button
                                    circular
                                    size={'mini'}
                                    icon={<PencilLine />}
                                    onClick={() => {
                                      setOpenEditAttributeSelectionModal(true)
                                      setEditingAttributeSelectionId(cell.id)
                                    }}
                                  ></Button>
                                  <Button
                                    circular
                                    color="red"
                                    size={'mini'}
                                    icon={<Trash />}
                                    onClick={() => {
                                      deleteAttributeSelection(cell.id)
                                    }}
                                  ></Button>
                                </Button.Group>
                              )}
                            </Table.Cell>
                          )
                        }
                      })}
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </>
          )}
        </Table>
      )}
    </div>
  )
}

export default AdminProductVariants
